/**
 * Personnel data utilities for route management
 */

import { WALKING_DISTANCE_TRESHOLD } from '@/modules/Flexigo/helpers/constants';
import { PeopleCategory } from '@/modules/Flexigo/constants/mapMarkers';
import { formatWalkingDistance } from '@/helpers/convert';

/**
 * Normalize personnel data to handle both direct and nested structures
 * @param {Object} personnelData - Personnel data object
 * @returns {Object} Normalized personnel data
 */
export const normalizePersonnelData = (personnelData) => {
  // Handle null or undefined personnel data
  if (!personnelData) {
    return null;
  }

  if (personnelData.personnel) {
    const { personnel, route, station } = personnelData;
    return {
      ...personnel,
      ...personnelData,
      routeId: route?.id,
      routeName: route?.name,
      stationId: station?.id,
      stationDescription: station?.description || station?.name,
      homeLocation: personnelData.homeLocation || personnel.homeLocation,
      category: personnelData.category,
    };
  }

  // Handle direct personnel structure: { id, name, routeName, stationDescription, ... }
  return personnelData;
};

/**
 * Check if personnel is within walking distance threshold
 * @param {Object} person - Personnel object
 * @returns {boolean} True if within walking distance
 */
export const isWithinWalkingDistance = (person) => {
  // Validate person object exists
  if (!person) {
    return false;
  }
  // If walkingDistanceInMeter is undefined or null, treat as within walking distance
  // Otherwise, check if it's within the threshold
  return (
    person.walkingDistanceInMeter === undefined ||
    person.walkingDistanceInMeter === null ||
    person.walkingDistanceInMeter <= WALKING_DISTANCE_TRESHOLD
  );
};

/**
 * Categorize personnel based on walking distance
 * @param {Object} person - Personnel object
 * @returns {string} Personnel category
 */
export const categorizePersonnelByWalkingDistance = (person) =>
  isWithinWalkingDistance(person) ? PeopleCategory.IN_WALKING_DISTANCE : PeopleCategory.OUT_OF_WALKING_DISTANCE;

/**
 * Format personnel data for info window display
 * @param {Object} personnel - Personnel object
 * @param {boolean} isVanpool - Whether it's vanpool mode
 * @param {string} minutesText - Text for minutes
 * @param {string} [lengthUnit='METRIC'] - Length unit ('METRIC' or 'IMPERIAL')
 * @returns {Object} Formatted personnel data
 */
export const formatPersonnelInfoData = (personnel, isVanpool, minutesText, lengthUnit = 'METRIC') => ({
  route: personnel.routeName || personnel.route?.name || '-',
  station: personnel.stationDescription || personnel.station?.description || '-',
  distanceToStop: formatWalkingDistance(personnel.walkingDistanceInMeter, lengthUnit),
  [isVanpool ? 'timeOnVanpool' : 'timeOnShuttle']: personnel.destinationTravelTimeInMin
    ? `${personnel.destinationTravelTimeInMin.toFixed(2)} ${minutesText}.`
    : '-',
  timeCoefficient: personnel.timeCoefficient ? personnel.timeCoefficient.toFixed(2) : '-',
});

export const getStationBaseData = (station) => ({
  id: station.id,
  name: station.name,
  description: station.description,
  location: station.location,
});
/**
 * Create personnel info object for display
 * @param {Object} personnelData - Personnel data
 * @param {string} [lengthUnit='METRIC'] - Length unit ('METRIC' or 'IMPERIAL')
 * @returns {Object} Personnel info object
 */
export const createPersonnelInfoObject = (personnelData, lengthUnit = 'METRIC') => {
  const normalized = normalizePersonnelData(personnelData);
  const {
    homeLocation,
    routeName,
    stationDescription,
    walkingDistanceInMeter,
    destinationTravelTimeInMin,
    timeCoefficient,
    name,
    surname,
  } = normalized;

  return {
    position: {
      lat: homeLocation.latitude,
      lng: homeLocation.longitude,
    },
    infoData: {
      routeName,
      station: stationDescription,
      distanceToStop: formatWalkingDistance(walkingDistanceInMeter, lengthUnit),
      destinationTravelTime: destinationTravelTimeInMin || '-',
      timeCoefficient: timeCoefficient ? timeCoefficient.toFixed(2) : '-',
    },
    ...normalized,
    fullName: `${name} ${surname || ''}`,
  };
};

/**
 * Process personnel in station data with normalization
 * @param {Array} routes - Array of route objects
 * @returns {Array} Processed personnel array
 */
export const processPersonnelWithStation = (routes) =>
  routes.flatMap((route) =>
    (route.stations || []).flatMap((station) =>
      (station.personnels || []).map((personnelInStation) => {
        // Normalize to a direct structure to avoid mixed nested/direct formats
        const normalized = normalizePersonnelData(
          personnelInStation.personnel
            ? personnelInStation
            : {
                personnel: personnelInStation,
                route,
                station,
                walkingDistanceInMeter: personnelInStation.walkingDistanceInMeter,
                destinationTravelTimeInMin: personnelInStation.destinationTravelTimeInMin,
                timeCoefficient: personnelInStation.timeCoefficient,
                homeLocation: personnelInStation.homeLocation,
              },
        );
        return {
          ...normalized,
          routeId: normalized.routeId || route.id,
          routeName: normalized.routeName || route.name,
          stationName: normalized.stationName || station.name,
          stationDescription: normalized.stationDescription || station.description,
          category: categorizePersonnelByWalkingDistance(normalized),
        };
      }),
    ),
  );
